<template>
  <div class="members-section">
    <div class="section-header">
      <h3>Team Members</h3>
      <button 
        v-if="canManageMembers" 
        class="btn btn-primary" 
        @click="showAddMember = true"
      >
        <span>👤</span> Add Member
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading members...</p>
    </div>

    <!-- Members List -->
    <div v-else class="members-list">
      <div v-if="members.length === 0" class="empty-state">
        <p>No members yet</p>
        <small>Add team members to collaborate on this workstream</small>
      </div>
      
      <div v-for="member in members" :key="member.id" class="member-item">
        <div class="member-avatar">
          {{ getInitials(getUserName(member.member_id)) }}
        </div>
        <div class="member-info">
          <div class="member-name">{{ getUserName(member.member_id) }}</div>
          <div class="member-email">{{ getUserEmail(member.member_id) }}</div>
          <div class="member-meta">
            Joined {{ formatDate(member.joined_at) }}
          </div>
        </div>
        <div class="member-role">
          <select 
            v-model="member.role" 
            @change="updateRole(member)"
            :disabled="member.role === 'lead' || !canManageMembers"
            class="role-select"
          >
            <option value="lead">Lead</option>
            <option value="contributor">Contributor</option>
            <option value="reviewer">Reviewer</option>
            <option value="observer">Observer</option>
          </select>
        </div>
        <div class="member-actions">
          <button 
            v-if="member.role !== 'lead' && canManageMembers"
            class="btn-remove"
            @click="removeMember(member)"
            title="Remove member"
          >
            ×
          </button>
        </div>
      </div>
    </div>

    <!-- Add Member Modal -->
    <div v-if="showAddMember" class="modal-overlay" @click.self="closeAddModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Add Team Member</h3>
          <button @click="closeAddModal" class="btn-close">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Search Users</label>
            <input 
              type="text" 
              v-model="searchQuery"
              @input="searchUsers"
              placeholder="Search by name or email..."
              class="search-input"
              ref="searchInput"
            />
            <div v-if="searchResults.length > 0" class="search-results">
              <div 
                v-for="user in searchResults" 
                :key="user.id"
                class="search-result-item"
                @click="selectUser(user)"
              >
                <div class="result-avatar">
                  {{ getInitials(user.name) }}
                </div>
                <div class="result-info">
                  <div class="result-name">{{ user.name }}</div>
                  <div class="result-email">{{ user.email }}</div>
                </div>
              </div>
            </div>
            <div v-else-if="searchQuery.length >= 2" class="no-results">
              No users found
            </div>
          </div>
          
          <div v-if="selectedUser" class="form-group">
            <label>Selected User</label>
            <div class="selected-user">
              <div class="selected-avatar">
                {{ getInitials(selectedUser.name) }}
              </div>
              <div class="selected-info">
                <div>{{ selectedUser.name }}</div>
                <div class="selected-email">{{ selectedUser.email }}</div>
              </div>
              <button @click="clearSelection" class="btn-clear">×</button>
            </div>
          </div>

          <div class="form-group">
            <label>Role</label>
            <select v-model="newMemberRole" class="role-select">
              <option value="contributor">Contributor</option>
              <option value="reviewer">Reviewer</option>
              <option value="observer">Observer</option>
              <option value="lead">Lead</option>
            </select>
            <small class="form-hint">
              <span v-if="newMemberRole === 'lead'">Can manage everything including members</span>
              <span v-else-if="newMemberRole === 'contributor'">Can create and edit tasks</span>
              <span v-else-if="newMemberRole === 'reviewer'">Can review and comment</span>
              <span v-else>Read-only access</span>
            </small>
          </div>

          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closeAddModal">Cancel</button>
            <button 
              class="btn btn-primary" 
              @click="addMember"
              :disabled="!selectedUser || adding"
            >
              {{ adding ? 'Adding...' : 'Add Member' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { workstreamsApi, usersApi } from '@/services/api'

export default {
  name: 'WorkstreamMembers',
  props: {
    workstreamId: {
      type: String,
      required: true
    },
    currentUserRole: {
      type: String,
      default: 'observer'
    }
  },
  setup(props, { emit }) {
    const members = ref([])
    const loading = ref(false)
    const adding = ref(false)
    const showAddMember = ref(false)
    const searchQuery = ref('')
    const searchResults = ref([])
    const selectedUser = ref(null)
    const newMemberRole = ref('contributor')
    const users = ref({}) // Cache user info
    const searchInput = ref(null)

    const canManageMembers = computed(() => {
      return ['lead', 'admin', 'owner'].includes(props.currentUserRole)
    })

    const loadMembers = async () => {
      try {
        loading.value = true
        const response = await workstreamsApi.getMembers(props.workstreamId)
        members.value = response.members || []

        // Seed local user cache from member payload (backend already returns user_name/user_email)
        for (const member of members.value) {
          const cached = users.value[member.member_id]
          const name = member.user_name || cached?.name || ''
          const email = member.user_email || cached?.email || ''
          if (name || email) {
            users.value[member.member_id] = {
              ...(cached || {}),
              id: member.member_id,
              name,
              email
            }
          }
        }

        // Fetch missing user info for members lacking name/email
        const missingUserIds = members.value
          .filter(m => {
            const u = users.value[m.member_id]
            return !u || (!u.name && !u.email)
          })
          .map(m => m.member_id)

        if (missingUserIds.length) {
          await Promise.all(missingUserIds.map(loadUserInfo))
        }

        emit('members-loaded', members.value)
      } catch (error) {
        console.error('Failed to load members:', error)
        members.value = []
      } finally {
        loading.value = false
      }
    }

    const loadUserInfo = async (userId) => {
      try {
        const response = await usersApi.getById(userId)
        const raw = response
        const user = raw?.user || raw
        const name = (
          user?.name ||
          user?.full_name ||
          user?.fullName ||
          user?.display_name ||
          [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
          (user?.email ? String(user.email).split('@')[0] : '') ||
          (user?.username ? String(user.username) : '') ||
          `User ${user?.id || userId}`
        )
        const email = user?.email || user?.username || ''
        users.value[userId] = { ...user, name, email }
      } catch (error) {
        // If the user truly doesn't exist anymore, avoid noisy error logs
        if (error?.response?.status !== 404) {
          console.error(`Failed to load user ${userId}:`, error)
        }
        users.value[userId] = { name: 'Deleted user', email: '' }
      }
    }

    const getUserName = (userId) => {
      const u = users.value[userId]
      if (!u) return 'Loading...'
      return (
        u.name || u.full_name || u.fullName || u.display_name ||
        [u.first_name, u.last_name].filter(Boolean).join(' ') ||
        (u.email ? String(u.email).split('@')[0] : '') ||
        (u.username ? String(u.username) : '') ||
        `User ${userId}`
      )
    }

    const getUserEmail = (userId) => {
      const u = users.value[userId]
      if (!u) return ''
      return u.email || u.username || ''
    }

    const getInitials = (name) => {
      if (!name || name === 'Loading...') return '?'
      return name
        .split(/[\s@]/)
        .filter(part => part)
        .map(part => part[0].toUpperCase())
        .slice(0, 2)
        .join('')
    }

    const formatDate = (date) => {
      if (!date) return 'Unknown'
      const d = new Date(date)
      const now = new Date()
      const diffTime = Math.abs(now - d)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) return 'today'
      if (diffDays === 1) return 'yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
      return d.toLocaleDateString()
    }

    const searchUsers = async () => {
      if (searchQuery.value.length < 2) {
        searchResults.value = []
        return
      }
      
      try {
        const response = await usersApi.search(searchQuery.value)
        console.log('User search response:', response)
        
        const allUsers = Array.isArray(response) ? response : (response?.users || [])
        console.log('Parsed users:', allUsers)
        
        // Filter out already added members
        const memberIds = members.value.map(m => m.member_id)
        searchResults.value = allUsers.filter(u => u && u.id && !memberIds.includes(u.id))
        console.log('Filtered search results:', searchResults.value)
      } catch (error) {
        console.error('Failed to search users:', error)
        searchResults.value = []
      }
    }

    const selectUser = (user) => {
      console.log('Selecting user:', user)
      selectedUser.value = user
      searchResults.value = []
      searchQuery.value = ''
    }

    const clearSelection = () => {
      selectedUser.value = null
      searchQuery.value = ''
      searchResults.value = []
    }

    const addMember = async () => {
      console.log('addMember called, selectedUser:', selectedUser.value)
      
      if (!selectedUser.value) {
        console.error('No user selected')
        return
      }
      
      if (!selectedUser.value.id) {
        console.error('Selected user has no id:', selectedUser.value)
        alert('Selected user is invalid. Please search and select a user again.')
        return
      }
      
      // Store values before clearing modal (to avoid null reference after closeAddModal)
      const userId = selectedUser.value.id
      const role = newMemberRole.value
      
      console.log('Adding member:', { userId, role })
      
      try {
        adding.value = true
        await workstreamsApi.addMember(props.workstreamId, {
          member_id: userId,
          role: role
        })
        
        closeAddModal()
        await loadMembers()
        emit('member-added', { userId, role })
      } catch (error) {
        console.error('Failed to add member:', error)
        const message = error.response?.data?.error || error.message
        alert(`Failed to add member: ${message}`)
      } finally {
        adding.value = false
      }
    }

    const updateRole = async (member) => {
      const previousRole = members.value.find(m => m.id === member.id)?.role
      try {
        await workstreamsApi.updateMemberRole(
          props.workstreamId,
          member.member_id,
          { role: member.role }
        )
        emit('role-updated', { userId: member.member_id, role: member.role })
      } catch (error) {
        console.error('Failed to update role:', error)
        alert('Failed to update role')
        // Revert to previous role
        if (previousRole) {
          member.role = previousRole
        }
        await loadMembers()
      }
    }

    const removeMember = async (member) => {
      const userName = getUserName(member.member_id)
      if (!confirm(`Remove ${userName} from this workstream?`)) {
        return
      }
      
      try {
        await workstreamsApi.removeMember(props.workstreamId, member.member_id)
        await loadMembers()
        emit('member-removed', member.member_id)
      } catch (error) {
        console.error('Failed to remove member:', error)
        const message = error.response?.data?.error || error.message
        alert(`Failed to remove member: ${message}`)
      }
    }

    const closeAddModal = () => {
      showAddMember.value = false
      clearSelection()
      newMemberRole.value = 'contributor'
    }

    // Focus search input when modal opens
    const openAddModal = async () => {
      showAddMember.value = true
      await nextTick()
      searchInput.value?.focus()
    }

    onMounted(() => {
      loadMembers()
    })

    return {
      members,
      loading,
      adding,
      showAddMember,
      searchQuery,
      searchResults,
      selectedUser,
      newMemberRole,
      canManageMembers,
      searchInput,
      getUserName,
      getUserEmail,
      getInitials,
      formatDate,
      searchUsers,
      selectUser,
      clearSelection,
      addMember,
      updateRole,
      removeMember,
      closeAddModal,
      openAddModal
    }
  }
}
</script>

<style scoped>
.members-section {
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-top: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #37352f;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #37352f;
  color: white;
  font-size: 0.85rem;
}

.btn-primary:hover:not(:disabled) {
  background: #2d2b27;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #37352f;
  border: 1px solid rgba(0,0,0,0.1);
}

.btn-secondary:hover {
  background: rgba(0,0,0,0.03);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #a0a0a0;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(0,0,0,0.06);
  border-top: 2px solid #37352f;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #a0a0a0;
}

.empty-state p {
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
}

.empty-state small {
  font-size: 0.85rem;
}

.members-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  transition: all 0.15s;
}

.member-item:hover {
  border-color: #dee2e6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.member-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
}

.member-info {
  flex: 1;
  min-width: 0;
}

.member-name {
  font-weight: 600;
  color: #37352f;
  font-size: 0.95rem;
  margin-bottom: 0.2rem;
}

.member-email {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.2rem;
}

.member-meta {
  font-size: 0.75rem;
  color: #a0a0a0;
}

.role-select {
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 3px;
  background: white;
  font-size: 0.85rem;
  color: #37352f;
  cursor: pointer;
  transition: all 0.15s;
}

.role-select:hover:not(:disabled) {
  border-color: rgba(0,0,0,0.2);
}

.role-select:disabled {
  background: #f0f0f0;
  cursor: not-allowed;
  color: #6c757d;
}

.btn-remove {
  width: 28px;
  height: 28px;
  border-radius: 3px;
  border: none;
  background: transparent;
  color: #dc3545;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}

.btn-remove:hover {
  background: rgba(220, 53, 69, 0.1);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 15, 15, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 3px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #37352f;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: all 0.15s;
}

.btn-close:hover {
  background: rgba(0,0,0,0.05);
}

.modal-body {
  padding: 1.25rem;
}

.form-group {
  margin-bottom: 1.25rem;
  position: relative;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.85rem;
  color: #37352f;
}

.search-input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 3px;
  font-size: 0.9rem;
  transition: border-color 0.15s;
}

.search-input:focus {
  outline: none;
  border-color: rgba(0,0,0,0.2);
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 3px;
  margin-top: 0.25rem;
  max-height: 240px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  transition: background 0.15s;
}

.search-result-item:hover {
  background: #f8f9fa;
}

.search-result-item:last-child {
  border-bottom: none;
}

.result-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.result-info {
  flex: 1;
  min-width: 0;
}

.result-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #37352f;
}

.result-email {
  font-size: 0.8rem;
  color: #6c757d;
}

.no-results {
  padding: 1rem;
  text-align: center;
  color: #a0a0a0;
  font-size: 0.85rem;
}

.selected-user {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #e7f3ff;
  border-radius: 6px;
  border: 1px solid #d0e9ff;
}

.selected-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.selected-info {
  flex: 1;
  min-width: 0;
}

.selected-info > div:first-child {
  font-weight: 500;
  color: #37352f;
  font-size: 0.9rem;
}

.selected-email {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.1rem;
}

.btn-clear {
  width: 24px;
  height: 24px;
  border-radius: 3px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}

.btn-clear:hover {
  background: rgba(0,0,0,0.1);
}

.form-hint {
  display: block;
  margin-top: 0.4rem;
  font-size: 0.75rem;
  color: #6c757d;
}

.modal-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}
</style>
